// src/components/ClassMetricsModal.js
// Admin-only per-class metrics drill-down modal.
// Loads metrics from /api/admin-class-metrics on mount and renders:
//   - Class summary header (title, type, status, duration, reach)
//   - Live attendance table (name | watch time | % | qualified | last seen)
//   - Completions table (name | source | completed at)
//   - Engagement (hearts, comments, reactions)
//
// Static load — admin clicks Refresh to re-fetch.
// See docs/decisions/adr-class-metrics.md for the design.

import React, { useEffect, useState, useCallback } from 'react';
import { fetchClassMetrics, getCachedMetrics } from '../services/classMetrics';

// ── Skeleton block style (2026-05-04) ──
// We deliberately don't use the global `mom-skeleton` class here — its
// cream→cream-dark gradient is invisible against this modal's `--mom-white`
// body background. Inline rgba stops give the proper visible shimmer
// without touching that global class (which is used in 9+ other places
// where its current contrast is acceptable for those backgrounds).
const SKELETON_BLOCK = {
    background: 'linear-gradient(90deg, rgba(0,0,0,0.04) 25%, rgba(0,0,0,0.08) 50%, rgba(0,0,0,0.04) 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s ease infinite',
    borderRadius: 8,
};
const sk = (extra) => ({ ...SKELETON_BLOCK, ...extra });

// ── Formatters ──
function formatSeconds(totalSec) {
    if (!Number.isFinite(totalSec) || totalSec < 0) return '—';
    const s = Math.round(totalSec);
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return `${m}:${String(rem).padStart(2, '0')}`;
}

function formatPercent(ratio) {
    if (!Number.isFinite(ratio)) return '—';
    return `${Math.round(ratio * 100)}%`;
}

function formatDateTime(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return '—';
        return d.toLocaleString('en-US', {
            month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit',
        });
    } catch (_) { return '—'; }
}

function formatScheduledAt(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return '—';
        return d.toLocaleString('en-US', {
            weekday: 'short', month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit',
        });
    } catch (_) { return '—'; }
}

function StatusBadge({ state, classType }) {
    // Color coding: live = primary (warm), ended = neutral, scheduled = sage,
    // cancelled = muted red. Matches the existing schedule-tile palette.
    const config = {
        scheduled: { label: 'Upcoming', bg: 'rgba(120, 162, 134, 0.12)', color: '#5a8266' },
        waiting_room_open: { label: 'Waiting Room Open', bg: 'rgba(120, 162, 134, 0.12)', color: '#5a8266' },
        live: { label: 'Live now', bg: 'color-mix(in srgb, var(--mom-primary) 14%, transparent)', color: 'var(--mom-primary-dark)' },
        ending: { label: 'Live now', bg: 'color-mix(in srgb, var(--mom-primary) 14%, transparent)', color: 'var(--mom-primary-dark)' },
        ended: { label: 'Aired', bg: 'rgba(120, 120, 120, 0.12)', color: 'var(--mom-warm-gray)' },
        cancelled: { label: 'Cancelled', bg: 'rgba(220, 38, 38, 0.10)', color: '#dc2626' },
    };
    const c = config[state] || { label: state || 'Unknown', bg: 'rgba(120, 120, 120, 0.12)', color: 'var(--mom-warm-gray)' };
    return (
        <span style={{
            fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            padding: '4px 10px', borderRadius: 12,
            background: c.bg, color: c.color,
        }}>
            {c.label}{classType === 'prerecorded' ? ' · Pre-recorded' : ''}
        </span>
    );
}

function SectionHeader({ title, subtitle, count }) {
    return (
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
            <div>
                <h3 style={{
                    fontFamily: 'var(--mom-font-serif)', fontSize: 18, fontStyle: 'italic',
                    fontWeight: 600, color: 'var(--mom-charcoal)', margin: 0,
                }}>
                    {title}
                </h3>
                {subtitle && (
                    <p style={{
                        fontFamily: 'var(--mom-font-sans)', fontSize: 13,
                        color: 'var(--mom-warm-gray)', margin: '2px 0 0',
                    }}>{subtitle}</p>
                )}
            </div>
            {Number.isFinite(count) && (
                <span style={{
                    fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 600,
                    color: 'var(--mom-charcoal)',
                    padding: '4px 12px', borderRadius: 12,
                    background: 'var(--mom-cream)',
                }}>
                    {count}
                </span>
            )}
        </div>
    );
}

function EmptyState({ children }) {
    return (
        <p style={{
            fontFamily: 'var(--mom-font-sans)', fontSize: 13,
            color: 'var(--mom-warm-gray)', fontStyle: 'italic',
            margin: '8px 0 0', padding: '16px 0',
        }}>
            {children}
        </p>
    );
}

// Stat card for the summary header — used for the small KPI tiles at the top.
function StatTile({ label, value, hint }) {
    return (
        <div style={{
            background: 'var(--mom-cream)', borderRadius: 14,
            padding: '14px 16px', flex: '1 1 140px', minWidth: 140,
        }}>
            <p style={{
                fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                color: 'var(--mom-warm-gray)', margin: 0,
            }}>{label}</p>
            <p style={{
                fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontWeight: 700,
                color: 'var(--mom-charcoal)', margin: '4px 0 0',
            }}>{value}</p>
            {hint && (
                <p style={{
                    fontFamily: 'var(--mom-font-sans)', fontSize: 11,
                    color: 'var(--mom-warm-gray)', margin: '2px 0 0',
                }}>{hint}</p>
            )}
        </div>
    );
}

function TableRow({ children, isHeader = false }) {
    return (
        <div style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(140px, 2fr) repeat(auto-fit, minmax(80px, 1fr))',
            gap: 8,
            padding: isHeader ? '8px 12px' : '10px 12px',
            borderBottom: isHeader ? '1.5px solid var(--mom-cream)' : '1px solid rgba(0,0,0,0.04)',
            fontFamily: 'var(--mom-font-sans)',
            fontSize: isHeader ? 11 : 13,
            fontWeight: isHeader ? 700 : 500,
            letterSpacing: isHeader ? '0.06em' : 'normal',
            textTransform: isHeader ? 'uppercase' : 'none',
            color: isHeader ? 'var(--mom-warm-gray)' : 'var(--mom-charcoal)',
            alignItems: 'center',
        }}>
            {children}
        </div>
    );
}

function ClassMetricsModal({ classId, getToken, onClose }) {
    // Seed initial state from cache (sync) so a repeat-open within the 30s
    // TTL renders instantly with no skeleton flicker. The mount effect below
    // still calls load() but its synchronous cache check short-circuits.
    const [data, setData] = useState(() => getCachedMetrics(classId));
    const [loading, setLoading] = useState(() => !getCachedMetrics(classId));
    const [error, setError] = useState(null);

    // load(force=false): use cache if fresh; force=true bypasses cache.
    // Refresh button passes force=true so admin can pull fresh data on demand.
    const load = useCallback(async (force = false) => {
        // On a cache hit (and not forcing), short-circuit synchronously to
        // avoid even a single-frame loading flicker. The setLoading(false)
        // covers the race where state was true at mount and cache populated
        // between ctor and this point.
        if (!force) {
            const cached = getCachedMetrics(classId);
            if (cached) {
                setData(cached);
                setError(null);
                setLoading(false);
                return;
            }
        }
        setLoading(true);
        setError(null);
        try {
            const result = await fetchClassMetrics(classId, getToken, { force });
            setData(result);
        } catch (e) {
            setError(e?.message || 'Failed to load metrics');
        } finally {
            setLoading(false);
        }
    }, [classId, getToken]);

    useEffect(() => { load(false); }, [load]);

    // Esc to close — admin-friendly.
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div className="mom-modal-overlay"
            style={{
                position: 'fixed', inset: 0, background: 'var(--mom-overlay)', zIndex: 1100,
                display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                padding: 'clamp(12px, 3vw, 32px)', overflowY: 'auto',
            }}
            onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()}
                style={{
                    background: 'var(--mom-white)', borderRadius: 24,
                    maxWidth: 880, width: '100%',
                    boxShadow: 'var(--shadow-xl)',
                    animation: 'modalIn 0.3s ease-out',
                    margin: 'auto 0',
                }}>
                {/* Header */}
                <div style={{
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                    padding: '20px 24px 16px',
                    borderBottom: '1px solid var(--mom-cream)',
                    gap: 12,
                }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <p style={{
                            fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700,
                            letterSpacing: '0.1em', textTransform: 'uppercase',
                            color: 'var(--mom-primary)', margin: 0,
                        }}>Class Metrics</p>
                        <h2 style={{
                            fontFamily: 'var(--mom-font-serif)', fontSize: 22, fontStyle: 'italic',
                            fontWeight: 700, color: 'var(--mom-charcoal)',
                            margin: '4px 0 0', lineHeight: 1.2, wordBreak: 'break-word',
                        }}>
                            {data?.class?.title || (loading ? 'Loading…' : 'Class')}
                        </h2>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                        <button onClick={() => load(true)} disabled={loading}
                            style={{
                                background: 'none', border: '1.5px solid var(--mom-cream)',
                                color: 'var(--mom-warm-gray)', padding: '6px 12px', borderRadius: 12,
                                fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600,
                                cursor: loading ? 'wait' : 'pointer',
                                opacity: loading ? 0.5 : 1,
                            }}>
                            {loading ? 'Loading…' : 'Refresh'}
                        </button>
                        <button onClick={onClose} aria-label="Close metrics"
                            style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                fontSize: 22, color: 'var(--mom-warm-gray)',
                                lineHeight: 1, padding: '4px 10px',
                            }}>×</button>
                    </div>
                </div>

                {/* Body */}
                <div style={{ padding: '20px 24px 28px' }}>
                    {loading && !data && (
                        // Structural skeleton — mirrors the actual layout (status line,
                        // KPI tile row, two table sections) so the modal feels like it's
                        // rendering content rather than spinning. Reduces perceived wait
                        // even when the actual server roundtrip is unchanged. Animation
                        // uses the existing `shimmer` keyframes from index.css.
                        <div aria-busy="true" aria-label="Loading metrics" style={{ padding: '4px 0 8px' }}>
                            {/* Status badge + scheduled-at line */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                                <div style={sk({ width: 80, height: 22, borderRadius: 12 })} />
                                <div style={sk({ width: 220, height: 14, borderRadius: 6 })} />
                            </div>
                            {/* KPI tile row — 6 tiles wrapping at 140px each */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 28 }}>
                                {[0, 1, 2, 3, 4, 5].map((i) => (
                                    <div key={i} style={sk({ flex: '1 1 140px', minWidth: 140, height: 78, borderRadius: 14 })} />
                                ))}
                            </div>
                            {/* Live attendance section */}
                            <div style={{ marginBottom: 28 }}>
                                <div style={sk({ width: 160, height: 22, borderRadius: 8, marginBottom: 14 })} />
                                {[0, 1, 2, 3].map((i) => (
                                    <div key={i} style={sk({ height: 38, borderRadius: 6, marginBottom: 6 })} />
                                ))}
                            </div>
                            {/* Completions section */}
                            <div>
                                <div style={sk({ width: 130, height: 22, borderRadius: 8, marginBottom: 14 })} />
                                {[0, 1, 2].map((i) => (
                                    <div key={i} style={sk({ height: 38, borderRadius: 6, marginBottom: 6 })} />
                                ))}
                            </div>
                        </div>
                    )}

                    {error && (
                        <div style={{
                            padding: '16px 18px', borderRadius: 14,
                            background: 'rgba(220, 38, 38, 0.06)',
                            color: '#dc2626',
                            fontFamily: 'var(--mom-font-sans)', fontSize: 13,
                            marginBottom: 20,
                        }}>
                            <strong>Couldn't load metrics:</strong> {error}
                        </div>
                    )}

                    {data && (
                        <>
                            {/* ── Class summary tiles ── */}
                            <div style={{ marginBottom: 28 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                                    <StatusBadge state={data.class.state} classType={data.class.classType} />
                                    <span style={{
                                        fontFamily: 'var(--mom-font-sans)', fontSize: 13,
                                        color: 'var(--mom-warm-gray)',
                                    }}>
                                        Scheduled: {formatScheduledAt(data.class.scheduledAt)}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                                    <StatTile
                                        label="Reach"
                                        value={data.reach.uniqueUsers}
                                        hint="unique people"
                                    />
                                    <StatTile
                                        label="Live attended"
                                        value={data.attendance.available ? data.attendance.total : '—'}
                                        hint={data.attendance.available
                                            ? `${data.attendance.qualified} qualified ≥75%`
                                            : 'data not available'}
                                    />
                                    <StatTile
                                        label="Completions"
                                        value={data.completions.total}
                                        hint="all sources"
                                    />
                                    <StatTile
                                        label="Hearts"
                                        value={data.engagement.likeCount}
                                    />
                                    <StatTile
                                        label="Comments"
                                        value={data.engagement.commentCount}
                                    />
                                    {(() => {
                                        // Suppress the "scheduled X" hint when the displayed duration
                                        // is the scheduled duration (upcoming class, no actual data
                                        // yet). When the class has aired, actualDurationSeconds
                                        // diverges from cls.duration, and the hint is informative.
                                        const actualMin = data.class.actualDurationSeconds > 0
                                            ? Math.round(data.class.actualDurationSeconds / 60)
                                            : null;
                                        const scheduledMin = data.class.scheduledDurationMinutes || null;
                                        const showHint = scheduledMin && actualMin && actualMin !== scheduledMin;
                                        return (
                                            <StatTile
                                                label="Duration"
                                                value={actualMin ? `${actualMin} min` : '—'}
                                                hint={showHint ? `scheduled ${scheduledMin}` : null}
                                            />
                                        );
                                    })()}
                                </div>
                            </div>

                            {/* ── Attendance section ──
                                Subtitle is explicit about table semantics — the table includes
                                everyone who attended live (qualifiers AND non-qualifiers), so
                                the "qualified ≥75%" callout is shown alongside the avg-watched
                                so the table contents aren't misleading. */}
                            <section style={{ marginBottom: 28 }}>
                                <SectionHeader
                                    title="Live attendance"
                                    subtitle={data.attendance.available
                                        ? `${data.attendance.qualified} qualified ≥75% · avg. watched ${formatPercent(data.attendance.averageWatchedPercent)}`
                                        : null}
                                    count={data.attendance.available ? data.attendance.total : null}
                                />
                                {!data.attendance.available ? (
                                    <EmptyState>
                                        Live attendance data is not available for classes that aired before 2026-04-27.
                                    </EmptyState>
                                ) : data.attendance.list.length === 0 ? (
                                    <EmptyState>No one has attended live yet.</EmptyState>
                                ) : (
                                    <div style={{ background: 'var(--mom-white)', borderRadius: 12, border: '1px solid var(--mom-cream)' }}>
                                        <TableRow isHeader>
                                            <span>Name</span>
                                            <span>Watch time</span>
                                            <span>%</span>
                                            <span>Qualified</span>
                                            <span>Last seen</span>
                                        </TableRow>
                                        {data.attendance.list.map((a) => (
                                            <TableRow key={a.userId}>
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {a.fullName}
                                                </span>
                                                <span>{formatSeconds(a.watchedSeconds)}</span>
                                                <span style={{
                                                    fontWeight: a.qualified ? 700 : 500,
                                                    color: a.qualified ? 'var(--mom-primary)' : 'var(--mom-warm-gray)',
                                                }}>{formatPercent(a.percent)}</span>
                                                <span>{a.qualified ? '✓' : '—'}</span>
                                                <span style={{ color: 'var(--mom-warm-gray)' }}>
                                                    {formatDateTime(a.lastUpdatedAt)}
                                                </span>
                                            </TableRow>
                                        ))}
                                    </div>
                                )}
                            </section>

                            {/* ── Completions section ── */}
                            <section style={{ marginBottom: 28 }}>
                                <SectionHeader
                                    title="Completions"
                                    subtitle="Live attendance + on-demand watching + manual marks"
                                    count={data.completions.total}
                                />
                                {data.completions.list.length === 0 ? (
                                    <EmptyState>No completions yet.</EmptyState>
                                ) : (
                                    <div style={{ background: 'var(--mom-white)', borderRadius: 12, border: '1px solid var(--mom-cream)' }}>
                                        <TableRow isHeader>
                                            <span>Name</span>
                                            <span>Source</span>
                                            <span>Completed at</span>
                                        </TableRow>
                                        {data.completions.list.map((c, idx) => (
                                            <TableRow key={`${c.userId}-${idx}`}>
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {c.fullName}
                                                </span>
                                                <span style={{
                                                    fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700,
                                                    letterSpacing: '0.06em', textTransform: 'uppercase',
                                                    color: c.source === 'live' ? 'var(--mom-primary-dark)' : 'var(--mom-warm-gray)',
                                                }}>
                                                    {c.source === 'live' ? 'Live' : 'On-demand'}
                                                </span>
                                                <span style={{ color: 'var(--mom-warm-gray)' }}>
                                                    {formatDateTime(c.completedAt)}
                                                </span>
                                            </TableRow>
                                        ))}
                                    </div>
                                )}
                            </section>

                            {/* ── On-demand viewing section ──
                                Cumulative across all sessions for each user-recording pair.
                                See docs/decisions/adr-on-demand-watch-tracking.md.
                                Defensive guard: data.onDemandViewing may be missing if the
                                metrics endpoint hasn't been redeployed yet (graceful degrade). */}
                            {data.onDemandViewing && (
                                <section style={{ marginBottom: 28 }}>
                                    <SectionHeader
                                        title="On-demand viewing"
                                        subtitle={data.onDemandViewing.total > 0
                                            ? `Avg. watched ${formatPercent(data.onDemandViewing.averageWatchedPercent)}`
                                            : null}
                                        count={data.onDemandViewing.total}
                                    />
                                    {data.onDemandViewing.list.length === 0 ? (
                                        <EmptyState>No on-demand viewing yet.</EmptyState>
                                    ) : (
                                        <div style={{ background: 'var(--mom-white)', borderRadius: 12, border: '1px solid var(--mom-cream)' }}>
                                            <TableRow isHeader>
                                                <span>Name</span>
                                                <span>Watch time</span>
                                                <span>%</span>
                                                <span>Sessions</span>
                                                <span>Last viewed</span>
                                            </TableRow>
                                            {data.onDemandViewing.list.map((w) => (
                                                <TableRow key={w.userId}>
                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {w.fullName}
                                                    </span>
                                                    <span>{formatSeconds(w.watchedSeconds)}</span>
                                                    {/* Cap displayed % at 100% — re-watching pushes the
                                                        raw ratio above 1. The actual watch time is the
                                                        more accurate signal anyway. */}
                                                    <span style={{
                                                        fontWeight: w.percent >= 0.75 ? 700 : 500,
                                                        color: w.percent >= 0.75 ? 'var(--mom-primary)' : 'var(--mom-warm-gray)',
                                                    }}>
                                                        {w.percent >= 1 ? '≥100%' : formatPercent(w.percent)}
                                                    </span>
                                                    <span>{w.sessionsCount}</span>
                                                    <span style={{ color: 'var(--mom-warm-gray)' }}>
                                                        {formatDateTime(w.lastWatchedAt)}
                                                    </span>
                                                </TableRow>
                                            ))}
                                        </div>
                                    )}
                                </section>
                            )}

                            {/* ── Engagement section ── */}
                            <section style={{ marginBottom: 8 }}>
                                <SectionHeader title="Engagement" />

                                {/* Reactions row */}
                                {(data.engagement.reactions.heart > 0
                                    || data.engagement.reactions.fire > 0
                                    || data.engagement.reactions.strong > 0
                                    || data.engagement.reactions.clap > 0) && (
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                                        {[
                                            { emoji: '❤️', count: data.engagement.reactions.heart, label: 'Love' },
                                            { emoji: '🔥', count: data.engagement.reactions.fire, label: 'Fire' },
                                            { emoji: '💪', count: data.engagement.reactions.strong, label: 'Strong' },
                                            { emoji: '👏', count: data.engagement.reactions.clap, label: 'Clap' },
                                        ].filter((r) => r.count > 0).map((r) => (
                                            <span key={r.label}
                                                title={r.label}
                                                style={{
                                                    fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600,
                                                    padding: '6px 14px', borderRadius: 18,
                                                    background: 'var(--mom-cream)', color: 'var(--mom-charcoal)',
                                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                                }}>
                                                <span style={{ fontSize: 16 }}>{r.emoji}</span>
                                                {r.count}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {/* Hearts list */}
                                <div style={{ marginBottom: 16 }}>
                                    <p style={{
                                        fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 700,
                                        letterSpacing: '0.06em', textTransform: 'uppercase',
                                        color: 'var(--mom-warm-gray)', margin: '0 0 8px',
                                    }}>
                                        Hearts ({data.engagement.favoritedBy.length})
                                    </p>
                                    {data.engagement.favoritedBy.length === 0 ? (
                                        <EmptyState>No hearts yet.</EmptyState>
                                    ) : (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                            {data.engagement.favoritedBy.map((f) => (
                                                <span key={f.userId}
                                                    style={{
                                                        fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 500,
                                                        padding: '5px 12px', borderRadius: 14,
                                                        background: 'var(--mom-cream)', color: 'var(--mom-charcoal)',
                                                    }}>
                                                    {f.fullName}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Comments list */}
                                <div>
                                    <p style={{
                                        fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 700,
                                        letterSpacing: '0.06em', textTransform: 'uppercase',
                                        color: 'var(--mom-warm-gray)', margin: '0 0 8px',
                                    }}>
                                        Comments ({data.engagement.comments.length})
                                    </p>
                                    {data.engagement.comments.length === 0 ? (
                                        <EmptyState>No comments yet.</EmptyState>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            {data.engagement.comments.map((c) => (
                                                <div key={c.id}
                                                    style={{
                                                        background: c.isCoach
                                                            ? 'color-mix(in srgb, var(--mom-primary) 8%, var(--mom-cream))'
                                                            : 'var(--mom-cream)',
                                                        borderRadius: 12,
                                                        padding: '10px 14px',
                                                    }}>
                                                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                                                        <span style={{
                                                            fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 700,
                                                            color: c.isCoach ? 'var(--mom-primary-dark)' : 'var(--mom-charcoal)',
                                                        }}>
                                                            {c.isCoach ? 'Danielle' : (c.userName || c.userInitials || 'User')}
                                                        </span>
                                                        <span style={{
                                                            fontFamily: 'var(--mom-font-sans)', fontSize: 11,
                                                            color: 'var(--mom-warm-gray)',
                                                        }}>
                                                            {formatDateTime(c.createdAt)}
                                                        </span>
                                                    </div>
                                                    <p style={{
                                                        fontFamily: 'var(--mom-font-sans)', fontSize: 13,
                                                        color: 'var(--mom-charcoal)', margin: 0, lineHeight: 1.5,
                                                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                                    }}>{c.text}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </section>

                            <p style={{
                                fontFamily: 'var(--mom-font-sans)', fontSize: 11,
                                color: 'var(--mom-warm-gray)', textAlign: 'right',
                                margin: '20px 0 0', fontStyle: 'italic',
                            }}>
                                Generated {formatDateTime(data.generatedAt)}
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ClassMetricsModal;
